// src/pages/AngebotsPage.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./AngebotsPage.css";
import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import LoginHintModal from "../components/LoginHintModal";
import SpeechIcon from "../components/SpeechIcon";
import { isNativeMobileApp, makeNativeImageEntries } from "../utils/nativeImagePicker";
import ImageSourceModal from "../components/ImageSourceModal";
import {
  getCurrentAuthUser,
  subscribeToAuthChanges,
} from "../services/authDataService";
import {
  getOfferByIdData,
  createOfferData,
  updateOfferData,
  uploadOfferImagesData,
} from "../services/offersDataService";
import { getUserProfileData } from "../services/profileDataService";

function AngebotsPage() {
  const MAX_IMAGES = 5;
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const editOfferIdFromQuery = searchParams.get("edit");
  const editOfferIdFromState = location?.state?.editOfferId || null;

  const editOfferId = editOfferIdFromState || editOfferIdFromQuery || null;
  const isEditMode = !!editOfferId;

  // 🔔 Offline-Status für Banner
  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [currentUser, setCurrentUser] = useState(null);
const [authChecked, setAuthChecked] = useState(false);

  // 🔐 Login-Hinweis
  const [showLoginHint, setShowLoginHint] = useState(false);

  // ✅ NEU: Adresse fehlt Popup
  const [showAddressMissingModal, setShowAddressMissingModal] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    kontaktwege: [],
    showExactLocation: false,
    images: [], // {type:"existing",url} oder {type:"new",file,preview}
    mainImageIndex: null,
  });

  const [profile, setProfile] = useState({
    name: "",
    street: "",
    plz: "",
    email: "",
    telephone: "",
  });

  // ✅ NEU: Original-Owner & Original-Angebotsdaten merken (für Admin-Edits ohne Überschreiben)
  const [originalOfferUserId, setOriginalOfferUserId] = useState(null);
  const [originalOfferSnapshot, setOriginalOfferSnapshot] = useState(null); 
  // enthält z.B. seller/contactEmail/contactTelephone/street/plz etc.

  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const [showImageLimitPopup, setShowImageLimitPopup] = useState(false);
  const [showImageSourceModal, setShowImageSourceModal] = useState(false);

  // ✅ EE-Style Modals statt alert
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [savingText, setSavingText] = useState("Angebot wird gespeichert…");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successText, setSuccessText] = useState("");

  const [uploading, setUploading] = useState(false);

  // 🔍 Verbindung zu Firebase testen (für OfflineBanner)
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (AngebotsPage):", err);
        setIsOffline(true);
      }
    };
    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

 // 🔐 Login prüfen → Hinweis anzeigen, wenn kein User
useEffect(() => {
  let unsubscribeRef = null;
  let mounted = true;

  const initAuth = async () => {
    try {
      const user = await getCurrentAuthUser();

      if (mounted) {
        setCurrentUser(user || null);
        setAuthChecked(true);
      }

      unsubscribeRef = await subscribeToAuthChanges((nextUser) => {
        if (!mounted) return;

        setCurrentUser(nextUser || null);
        setAuthChecked(true);
      });
    } catch (error) {
      console.warn("AngebotsPage auth init failed:", error);

      if (mounted) {
        setCurrentUser(null);
        setAuthChecked(true);
      }
    }
  };

  initAuth();

  return () => {
    mounted = false;

    if (typeof unsubscribeRef === "function") {
      unsubscribeRef();
    }
  };
}, []);

useEffect(() => {
  if (!authChecked) return;

  setShowLoginHint(!currentUser);
}, [authChecked, currentUser]);

  // ✅ Helfer: "86943, Thaining" -> { plz5:"86943", city:"Thaining", raw:"86943, Thaining" }
  const parsePlzCity = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return { plz5: "", city: "", raw: "" };

    const match = raw.match(/\b(\d{5})\b/);
    const plz5 = match ? match[1] : "";

    let city = "";
    if (raw.includes(",")) {
      city = raw.split(",").slice(1).join(",").trim();
    } else {
      city = raw.replace(plz5, "").trim();
    }

    return { plz5, city, raw };
  };

  const normalizeStreetSpacing = (street) => {
  const s = String(street || "").trim();
  if (!s) return "";

  return s
    .replace(/([A-Za-zÄÖÜäöüß])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Za-zÄÖÜäöüß])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
};

  // ✅ Hausnummer entfernen: "Schmiedgasse 16" -> "Schmiedgasse"
const removeHouseNumber = (street) => {
  const s = normalizeStreetSpacing(street);
  if (!s) return "";

  const m = s.match(/^(.*?)(?:\s*\d+\w*(?:[-/]\d+\w*)?.*)$/);
  const base = m ? m[1] : s;

  // ✅ entfernt auch abschließende Punkte/Kommas/Spaces
  return base.replace(/[.,\s]+$/g, "").trim();

  };

  // Profil-Daten laden: zuerst Firestore, sonst localStorage-Fallback
  useEffect(() => {
  const loadProfile = async () => {
    const user = currentUser || null;

    if (user?.uid) {
      try {
        const data = await getUserProfileData(user.uid);

        if (data) {
          setProfile({
            name: data.name || data.username || "",
            street: data.street || "",
            plz: data.plz || "",
            email: data.email || user.email || "",
            telephone: data.telephone || "",
          });
          return;
        }
      } catch (err) {
        console.warn("⚠️ Profil nicht aus Service geladen, nutze localStorage:", err);
      }
    }

    const userLocal = JSON.parse(localStorage.getItem("userProfile") || "{}");

    setProfile({
      name: userLocal.name || userLocal.username || "",
      street: userLocal.street || "",
      plz: userLocal.plz || "",
      email: userLocal.email || "",
      telephone: userLocal.telephone || "",
    });
  };

  loadProfile();
}, [currentUser]);

  // ✅ Edit: Angebot laden und Formular füllen (inkl. Bilder)
  useEffect(() => {
  const loadOfferForEdit = async () => {
    if (!isEditMode) return;

    try {
      const data = await getOfferByIdData(editOfferId);

      if (!data) {
        console.warn("Edit-Angebot nicht gefunden:", editOfferId);
        return;
      }

      setOriginalOfferUserId(
  data.userId ||
  data.ownerId ||
  data.sellerId ||
  data?.seller?.uid ||
  data?.seller?.userId ||
  data?.seller?.ownerId ||
  null
);

      setOriginalOfferSnapshot({
  userId: data.userId || null,
  ownerId: data.ownerId || null,
  sellerId: data.sellerId || null,
  seller: data.seller || null,
        contactEmail: data.contactEmail || "",
        contactTelephone: data.contactTelephone || "",
        street: data.street || "",
        plz: data.plz || "",
        city: data.city || "",
        plzRaw: data.plzRaw || "",
        adresse: data.adresse || "",
        coords: data.coords || null,
      });

      const existingImages = Array.isArray(data.images)
        ? data.images.filter(Boolean).map((url) => ({ type: "existing", url }))
        : [];

      setFormData({
        title: data.title || "",
        category: data.category || "",
        description: data.description || "",
        kontaktwege: Array.isArray(data.kontaktwege) ? data.kontaktwege : [],
        showExactLocation: !!data.showExactLocation,
        images: existingImages,
        mainImageIndex:
          typeof data.mainImageIndex === "number" ? data.mainImageIndex : null,
      });

      setProfile((prev) => ({
        ...prev,
        name: prev.name || data?.seller?.name || data?.anbieterName || "",
        street: prev.street || data.street || data?.seller?.street || "",
        plz: prev.plz || data.plz || data?.seller?.plz || "",
        email: prev.email || data.contactEmail || data?.seller?.email || "",
        telephone: prev.telephone || data.contactTelephone || data?.seller?.telefon || "",
      }));
    } catch (err) {
      console.warn("Edit-Angebot laden fehlgeschlagen:", err);
    }
  };

  loadOfferForEdit();
}, [isEditMode, editOfferId]);

  const imagesCount = formData.images.length;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "kontaktwege") {
      setFormData((prev) => {
        const alreadySelected = prev.kontaktwege.includes(value);
        return {
          ...prev,
          kontaktwege: alreadySelected
            ? prev.kontaktwege.filter((v) => v !== value)
            : [...prev.kontaktwege, value],
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

const handleImageUpload = async (e, source = "gallery") => {
  const remainingSlots = MAX_IMAGES - formData.images.length;

  if (remainingSlots <= 0) {
    setShowImageLimitPopup(true);
    return;
  }

  let newItems = [];

  if (isNativeMobileApp() && (!e?.target?.files || e.target.files.length === 0)) {
    try {
      newItems = await makeNativeImageEntries({
        prefix: "angebot",
        source,
        limit: remainingSlots,
      });

      if (!Array.isArray(newItems) || newItems.length === 0) return;
    } catch (err) {
      console.warn("Native Bildauswahl abgebrochen:", err);
      return;
    }
  } else {
    const files = Array.from(e.target.files || []);

    if (files.length > remainingSlots) {
      setShowImageLimitPopup(true);
      return;
    }

    const validFiles = files.slice(0, remainingSlots);

    newItems = validFiles.map((file) => ({
      type: "new",
      file,
      preview: URL.createObjectURL(file),
    }));
  }

  setFormData((prev) => ({
    ...prev,
    images: [...prev.images, ...newItems],
    mainImageIndex:
      prev.mainImageIndex === null && prev.images.length === 0 && newItems.length > 0
        ? 0
        : prev.mainImageIndex,
  }));

  setFileInputKey(Date.now());

  if (e?.target) {
    e.target.value = null;
  }
};

  const handleImageDelete = (index) => {
    setFormData((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);

      let newMain = prev.mainImageIndex;

if (newMain === index) {
  newMain = newImages.length > 0 ? 0 : null;
} else if (newMain != null && newMain > index) {
  newMain = newMain - 1;
}

      return { ...prev, images: newImages, mainImageIndex: newMain };
    });
  };

  const handleMainImageSelect = (index) => {
    setFormData((prev) => ({ ...prev, mainImageIndex: index }));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("imageIndex", index);
  };

  const handleDrop = (e, dropIndex) => {
    const dragIndex = parseInt(e.dataTransfer.getData("imageIndex"), 10);
    if (Number.isNaN(dragIndex) || dragIndex === dropIndex) return;

    const updatedImages = [...formData.images];
    const [movedImage] = updatedImages.splice(dragIndex, 1);
    updatedImages.splice(dropIndex, 0, movedImage);

    setFormData((prev) => ({
      ...prev,
      images: updatedImages,
      mainImageIndex:
        prev.mainImageIndex === dragIndex
          ? dropIndex
          : prev.mainImageIndex === dropIndex
          ? dragIndex
          : prev.mainImageIndex,
    }));
  };

  const handleDragOver = (e) => e.preventDefault();

  // ✅ Upload nur für neue Bilder – existing URLs bleiben
  
const uploadNewImagesToStorage = async (userIdForPath) => {
  return await uploadOfferImagesData(formData.images, userIdForPath || "anonymous");
};

  // ✅ OSM/Nominatim Geocode (robust)
  const geocodeAddressToCoords = async ({ street, plzRaw, showExactLocation }) => {
    const streetRaw = normalizeStreetSpacing(street);
    const { plz5, city } = parsePlzCity(plzRaw);

    if (!streetRaw && !plz5 && !city) return null;

    const streetForSearch = showExactLocation ? streetRaw : removeHouseNumber(streetRaw);

    const queryParts = [
      streetForSearch,
      [plz5, city].filter(Boolean).join(" ").trim(),
      "Germany",
    ].filter(Boolean);

    const q = queryParts.join(", ");

    const url =
      `https://nominatim.openstreetmap.org/search` +
      `?format=json` +
      `&limit=1` +
      `&countrycodes=de` +
      `&q=${encodeURIComponent(q)}`;

    const res = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Accept-Language": "de",
      },
    });

    if (!res.ok) throw new Error("nominatim-failed");

    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;

    const lat = Number(data[0].lat);
    const lon = Number(data[0].lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return { lat, lng: lon };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = currentUser || null;
    if (!user) {
      setShowLoginHint(true);
      return;
    }

    // ✅ NEU: Adresse prüfen (Profil muss street + plz haben)
    const street = (profile.street || "").trim();
const plz = (profile.plz || "").trim();

if (!street || !plz) {
  setShowSavingModal(false);
  setUploading(false);
  setShowAddressMissingModal(true);
  return;
}

    setShowSavingModal(true);
    const hasImages = formData.images.some((item) => item?.type === "new");

setSavingText(
  hasImages
    ? isEditMode
      ? "Angebot wird aktualisiert. Der Bilder-Upload kann einen Moment dauern…"
      : "Angebot wird erstellt. Der Bilder-Upload kann einen Moment dauern…"
    : isEditMode
    ? "Angebot wird aktualisiert…"
    : "Angebot wird gespeichert…"
);
    setUploading(true);

    try {
      const editorUid = user.uid;

      if (!formData.kontaktwege || formData.kontaktwege.length === 0) {
        setShowSavingModal(false);
        setUploading(false);
        setSuccessText("Bitte wähle mindestens einen Kontaktweg aus.");
        setShowSuccessModal(true);
        return;
      }

      // ✅ Owner-ID: bei Edit immer original beibehalten
      const ownerUid = isEditMode
  ? (
      originalOfferUserId ||
      originalOfferSnapshot?.userId ||
      originalOfferSnapshot?.ownerId ||
      originalOfferSnapshot?.sellerId ||
      originalOfferSnapshot?.seller?.uid ||
      originalOfferSnapshot?.seller?.userId ||
      editorUid
    )
  : editorUid;

      // ✅ Uploadpfad: nutzt Owner (damit Bilder “dem Angebot” zugeordnet bleiben)
      const imageUrls = await uploadNewImagesToStorage(ownerUid);

        let coords = null;
        if (!isNativeMobileApp()) {
        coords = await geocodeAddressToCoords({
        street: profile.street,
        plzRaw: profile.plz,
        showExactLocation: formData.showExactLocation,
        }).catch((geoErr) => {
        console.warn("Geocoding fehlgeschlagen, speichere ohne coords:", geoErr);
        return null;
        });
}

      const mainIndex =
        formData.mainImageIndex !== null ? formData.mainImageIndex : imageUrls.length > 0 ? 0 : null;

      // ✅ PLZ normalisieren
      const { plz5, city, raw } = parsePlzCity(profile.plz);

      // Adresse (Anzeige)
      const streetDisplay = formData.showExactLocation ? profile.street : removeHouseNumber(profile.street);
      const addressDisplay = `${streetDisplay}${plz5 ? ", " + plz5 : ""}${city ? " " + city : ""}`.trim();

      // ✅ seller/contact beim Edit NICHT aus Admin-Profil überschreiben,
      // sondern aus dem originalen Angebot nehmen (wenn vorhanden).
      const originalSeller = originalOfferSnapshot?.seller || null;

      const sellerBlock = isEditMode
        ? (originalSeller || {
            name: profile.name || "",
            email: profile.email || "",
            telefon: profile.telephone || "",
            street: profile.street || "",
            plz: plz5 || "",
            city: city || "",
            showHouseNumber: !!formData.showExactLocation,
          })
        : {
            name: profile.name || "",
            email: profile.email || "",
            telefon: profile.telephone || "",
            street: profile.street || "",
            plz: plz5 || "",
            city: city || "",
            showHouseNumber: !!formData.showExactLocation,
          };

      const contactEmail = isEditMode
        ? (originalOfferSnapshot?.contactEmail || sellerBlock?.email || "")
        : (profile.email || "");

      const contactTelephone = isEditMode
        ? (originalOfferSnapshot?.contactTelephone || sellerBlock?.telefon || "")
        : (profile.telephone || "");

      const payload = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        kontaktwege: formData.kontaktwege,
        showExactLocation: formData.showExactLocation,

        seller: {
  ...sellerBlock,

  // ✅ Wichtig für Blockieren / Filtern / spätere Rechteprüfung
  uid: ownerUid,
  userId: ownerUid,
  ownerId: ownerUid,
},

adresse: addressDisplay,
plz: plz5 || "",
city: city || "",
plzRaw: raw || "",
street: profile.street || "",

coords: coords ? { lat: coords.lat, lng: coords.lng } : null,

contactEmail,
contactTelephone,

images: imageUrls,
mainImageIndex: mainIndex,

// ✅ Einheitliche Owner-Felder für alle Seiten
userId: ownerUid,
ownerId: ownerUid,
sellerId: ownerUid,

// ✅ Optional, aber hilfreich für Admin/Moderation
sellerName: sellerBlock?.name || profile.name || "",

    
      };

      if (isEditMode) {
  await updateOfferData(editOfferId, payload);
  setSuccessText("Dein Angebot wurde erfolgreich aktualisiert!");
} else {
  await createOfferData(payload);
  setSuccessText("Dein Angebot wurde erfolgreich erstellt!");
}

      setShowSavingModal(false);
      setUploading(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("❌ Fehler beim Speichern:", error);
      setShowSavingModal(false);
      setUploading(false);
      setSuccessText(
        "Dein Angebot konnte nicht gespeichert werden. Bitte prüfe deine Internetverbindung und versuche es erneut."
      );
      setShowSuccessModal(true);
    }
  };

  // ✅ Dummy Click: KEIN capture → Browser kann Kamera oder Galerie anbieten (so zuverlässig wie möglich)
const handleDummyClick = () => {
  if (isNativeMobileApp()) {
    setShowImageSourceModal(true);
    return;
  }

  if (fileInputRef.current) {
    fileInputRef.current.value = null;
    fileInputRef.current.removeAttribute("capture");
    fileInputRef.current.click();
  }
};

const handleChooseCamera = () => {
  setShowImageSourceModal(false);

  requestAnimationFrame(() => {
    handleImageUpload({}, "camera");
  });
};

const handleChooseGallery = () => {
  setShowImageSourceModal(false);

  requestAnimationFrame(() => {
    handleImageUpload({}, "gallery");
  });
};

  const renderPreviewSrc = (item) => {
    if (!item) return "/angebot-placeholder.png";
    if (item.type === "existing") return item.url;
    if (item.type === "new") return item.preview;
    return "/angebot-placeholder.png";
  };

  return (
    <div className="AngebotsPage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}
      {showLoginHint && <LoginHintModal onClose={() => setShowLoginHint(false)} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo no-tap-highlight"
        onClick={() => navigate("/")}
      />
      <SpeechIcon className="speech-icon no-tap-highlight" />
      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon no-tap-highlight"
        onClick={() => navigate("/rezepte")}
      />
      <p className="top-claim no-tap-highlight">Nachhaltigkeit – ganz einfach!</p>
      <GateIcon className="login-icon" />

      <div className="angebot-content">
        <div className="datenschutz-box">
          <p className="datenschutz-hinweis">
            Zwecks des Datenschutzes kannst du auswählen, ob deine Hausnummer im Angebot/auf der Karte sichtbar ist.
            Wenn nicht, wird der Marker nur auf die Straße gesetzt.
          </p>
        </div>

        <div className="image-upload-section">
          {imagesCount === 0 && (
            <div className="image-dummy-wrapper no-tap-highlight" onClick={handleDummyClick}>
              <img src="/angebot-placeholder.png" alt="Bild hinzufügen" className="image-dummy" />
            </div>
          )}

          <input
            key={fileInputKey}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            ref={fileInputRef}
            style={{ display: "none" }}
          />

          <div className="image-preview no-tap-highlight">
  {formData.images.map((item, i) => (
    <div
      key={i}
      className={`image-item ${formData.mainImageIndex === i ? "main-image" : ""}`}
      draggable
      onDragStart={(e) => handleDragStart(e, i)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => handleDrop(e, i)}
    >
      <img src={renderPreviewSrc(item)} alt={`preview-${i}`} />
      <div className="image-actions">
        <button type="button" onClick={() => handleImageDelete(i)}>X</button>
        <button type="button" onClick={() => handleMainImageSelect(i)}>★</button>
      </div>
    </div>
  ))}

  {formData.images.length > 0 && formData.images.length < MAX_IMAGES && (
    <div
      className="angebot-preview-add-image-item"
      onClick={handleDummyClick}
    >
      <img src="/upload-icon.png" alt="Weiteres Bild hinzufügen" />
    </div>
  )}
</div>

          {showImageLimitPopup && (
            <div className="modal-overlay" onClick={() => setShowImageLimitPopup(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <p>Maximal 5 Bilder erlaubt.</p>
                <button className="green-button" onClick={() => setShowImageLimitPopup(false)}>OK</button>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="angebot-form">
          <input
            type="text"
            name="title"
            placeholder="Titel des Angebots"
            value={formData.title}
            onChange={handleChange}
            required
          />

          <select name="category" value={formData.category} onChange={handleChange} required>
            <option value="">Angebotsart</option>
            <option value="verschenken">Verschenken</option>
            <option value="tauschen">Tauschen</option>
            <option value="verkaufen">Verkaufen</option>
            <option value="verschenken, tauschen">Verschenken oder Tauschen</option>
            <option value="verkaufen, tauschen">Verkaufen oder Tauschen</option>
          </select>

          <textarea
            name="description"
            placeholder="Beschreibung"
            value={formData.description}
            onChange={handleChange}
            required
          />

          <div
            className="checkbox-container"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                showExactLocation: !prev.showExactLocation,
              }))
            }
          >
            <input
              type="checkbox"
              name="showExactLocation"
              checked={formData.showExactLocation}
              onChange={() => {}}
            />
            <label>Hausnummer auf Karte/in der Anzeige sichtbar machen</label>
          </div>

          <div className="kontaktwege-section">
            <p>Kontaktweg wählen:</p>

            <label>
              <input
                type="checkbox"
                name="kontaktwege"
                value="E-Mail"
                checked={formData.kontaktwege.includes("E-Mail")}
                onChange={handleChange}
              />{" "}
              E-Mail
            </label>

            <label>
              <input
                type="checkbox"
                name="kontaktwege"
                value="Telefon"
                checked={formData.kontaktwege.includes("Telefon")}
                onChange={handleChange}
              />{" "}
              Telefon
            </label>

            <label>
              <input
                type="checkbox"
                name="kontaktwege"
                value="Plattform"
                checked={formData.kontaktwege.includes("Plattform")}
                onChange={handleChange}
              />{" "}
              Plattformnachricht
            </label>
          </div>

          <button type="submit" disabled={uploading}>
            {uploading
              ? isEditMode
                ? "Angebot wird aktualisiert…"
                : "Angebot wird gespeichert…"
              : isEditMode
              ? "Angebot aktualisieren"
              : "Angebot erstellen"}
          </button>
        </form>
      </div>

      {/* ✅ NEU: Adresse fehlt Modal */}
      {showAddressMissingModal && (
        <div className="modal-overlay" onClick={() => setShowAddressMissingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Adresse fehlt</h3>
            <p>
              Bitte ergänze zuerst deine Adresse in deinem Profil,
              bevor du ein Angebot speichern kannst.
            </p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setShowAddressMissingModal(false)}>
                Abbrechen
              </button>
              <button
                className="green-button"
                onClick={() => {
                  setShowAddressMissingModal(false);
                  navigate("/profil");
                }}
              >
                Zu deinem Profil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ EE Saving Modal */}
      {showSavingModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{savingText}</h3>
            <p>Bitte einen Moment…</p>
          </div>
        </div>
      )}

      {/* ✅ EE Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Info</h3>
            <p>{successText}</p>
            <div className="modal-buttons">
              <button
  className="green-button"
  onClick={() => {
    setShowSuccessModal(false);

    if (
      successText === "Dein Angebot wurde erfolgreich erstellt!" ||
      successText === "Dein Angebot wurde erfolgreich aktualisiert!"
    ) {
      navigate("/profil");
    }
  }}
>
  OK
</button>
            </div>
          </div>
        </div>
      )}
      <ImageSourceModal
  isOpen={showImageSourceModal}
  onClose={() => setShowImageSourceModal(false)}
  onCamera={handleChooseCamera}
  onGallery={handleChooseGallery}
/>
    </div>
  );
}

export default AngebotsPage;